// People and Membership contracts (PER-*, ADM-*).
//
// A Person is the durable human record. A Membership is the current access grant.
// Keeping those as separate wire concepts is what lets a proxy-only relative exist
// without a Clerk account, and what lets access be revoked without erasing their
// commitments, availability or audit attribution (PER-001, PER-002, PER-009, PER-010).

import { z } from "zod";

import { ALL_CAPABILITIES, ARCHETYPE_CAPABILITIES, type Capability } from "../capabilities.js";
import type { CommonRejection, Result } from "../errors.js";
import { circleIdSchema, personIdSchema } from "../primitives.js";

/** The three presets V1 presents. "Views only" remains an ordinary Member preset. */
export const membershipPresetSchema = z.enum(["organiser", "helper", "viewsOnly"]);
export type MembershipPreset = z.infer<typeof membershipPresetSchema>;

const capabilitySchema = z.custom<Capability>(
  (value) => typeof value === "string" && ALL_CAPABILITIES.includes(value as Capability),
  "Choose a known capability.",
);

const capabilityListSchema = z
  .array(capabilitySchema)
  .max(ALL_CAPABILITIES.length)
  .superRefine((capabilities, ctx) => {
    if (new Set(capabilities).size !== capabilities.length) {
      ctx.addIssue({ code: "custom", message: "A capability may be selected only once." });
    }
  });

const chosenNameSchema = z.string().trim().min(1, "A person needs a name.").max(120);
const relationshipLabelSchema = z.string().trim().min(1).max(80);

const emailContactSchema = z.object({
  kind: z.literal("email"),
  destination: z
    .email("Enter a complete email address.")
    .max(320)
    .transform((value) => value.trim().toLowerCase()),
});

const smsContactSchema = z.object({
  kind: z.literal("sms"),
  destination: z
    .string()
    .trim()
    .regex(/^\+[1-9]\d{7,14}$/u, "Enter a phone number with its country code.")
    .transform((value) => value.replace(/[\s().-]/gu, "")),
});

export const contactMethodInputSchema = z.discriminatedUnion("kind", [
  emailContactSchema,
  smsContactSchema,
]);

/** Add an offline/proxy-only person. Contact methods are optional by design. */
export const createPersonInput = z.object({
  circleId: circleIdSchema,
  chosenName: chosenNameSchema,
  relationshipLabel: relationshipLabelSchema.optional(),
  preset: membershipPresetSchema,
  capabilities: capabilityListSchema,
  contactMethods: z.array(contactMethodInputSchema).max(2).default([]),
});

/** Change the durable Person facts without touching their access grant. */
export const updatePersonInput = z
  .object({
    circleId: circleIdSchema,
    personId: personIdSchema,
    chosenName: chosenNameSchema.optional(),
    relationshipLabel: relationshipLabelSchema.nullable().optional(),
  })
  .refine(
    (input) => input.chosenName !== undefined || input.relationshipLabel !== undefined,
    "Change at least one person detail.",
  );

/** Apply a preset plus explicit fine-grained overrides to the Membership record. */
export const updateMembershipInput = z.object({
  circleId: circleIdSchema,
  personId: personIdSchema,
  preset: membershipPresetSchema,
  capabilities: capabilityListSchema,
});

export const deactivateMembershipInput = z.object({
  circleId: circleIdSchema,
  personId: personIdSchema,
});

export const listPeopleInput = z.object({ circleId: circleIdSchema });

export type CreatePersonInput = z.input<typeof createPersonInput>;
export type UpdatePersonInput = z.input<typeof updatePersonInput>;
export type UpdateMembershipInput = z.input<typeof updateMembershipInput>;
export type DeactivateMembershipInput = z.input<typeof deactivateMembershipInput>;
export type ContactMethodInput = z.output<typeof contactMethodInputSchema>;

export type PeopleRejection =
  | CommonRejection
  | { readonly kind: "duplicate_contact" }
  | { readonly kind: "last_organiser" }
  | { readonly kind: "self_grant_forbidden" }
  | { readonly kind: "capability_grant_exceeds_actor" };

export interface MaskedContactMethodView {
  readonly contactMethodId: string;
  readonly kind: "sms" | "email";
  /** The only destination shape this surface ever returns (PER-013). */
  readonly maskedDestination: string;
}

export interface PersonMembershipView {
  readonly personId: string;
  readonly membershipId: string;
  readonly chosenName: string;
  readonly relationshipLabel?: string;
  readonly participationState: "active" | "invited" | "offline" | "removed";
  readonly role: "organiser" | "member";
  readonly accessState: "pending" | "active" | "suspended" | "revoked";
  /** No Clerk link: this person participates through attributed proxy actions. */
  readonly proxyOnly: boolean;
  /** Null for viewers who cannot manage people; never infer permissions from role. */
  readonly capabilities: readonly Capability[] | null;
  readonly contactMethods: readonly MaskedContactMethodView[];
  /** Preview before revocation (PER-011); commitments themselves remain historical. */
  readonly futureCommitmentCount: number;
}

/**
 * The supported person, as every surface that names her must receive her.
 *
 * ONE SHAPE, ONE RECORD (bead hf-86o). The supported person is reached by exactly one
 * path — `circles.supportedPersonId` -> `supportedPeople` -> `people.chosenName` — and
 * three server reads consume her: the live plan, the circle settings, and the ICS feed.
 * Before this contract existed each resolved that chain for itself and returned a
 * different shape, which is how the calendar feed came to derive her initial from the
 * LAST CHARACTER of a plan title an organiser had typed.
 *
 * `isPlaceholder` travels WITH the name rather than beside it, deliberately. It is true
 * until the founder confirms the real spelling (D-091, bead
 * `hf-e10-hardening-pilot-f4o.5`), and a surface that received the name without it
 * would have no way to know it was rendering a guess. Paper is the case that matters:
 * a printout on a kitchen wall carries no notice unless this field put one there.
 */
export interface SupportedPersonView {
  readonly personId: string;
  readonly chosenName: string;
  /** The organiser's own words for how to reach them. Printouts only (PRT-004). */
  readonly printContactText?: string;
  /** The chosen default for who they should check with on their own printed sheet (§20.4). */
  readonly printContactPersonName?: string;
  readonly isPlaceholder: boolean;
}

export interface PeopleDirectoryView {
  readonly people: readonly PersonMembershipView[];
  readonly canManage: boolean;
}

export interface PersonWritten {
  readonly personId: string;
  readonly membershipId: string;
}

export interface PersonUpdated {
  readonly personId: string;
}

export interface MembershipUpdated {
  readonly personId: string;
  readonly membershipId: string;
  readonly accessState: "active" | "revoked";
  readonly futureCommitmentCount: number;
}

export type CreatePersonResult = Result<PersonWritten, PeopleRejection>;
export type UpdatePersonResult = Result<PersonUpdated, PeopleRejection>;
export type UpdateMembershipResult = Result<MembershipUpdated, PeopleRejection>;
export type DeactivateMembershipResult = Result<MembershipUpdated, PeopleRejection>;

/** The preset is convenience only; the explicit Membership array remains authority. */
export function capabilitiesForPreset(preset: MembershipPreset): readonly Capability[] {
  switch (preset) {
    case "organiser":
      return ARCHETYPE_CAPABILITIES.organiser;
    case "helper":
      return ARCHETYPE_CAPABILITIES.member;
    case "viewsOnly":
      return ARCHETYPE_CAPABILITIES.viewOnly;
  }
}

/** Role is V1 onboarding vocabulary, never the authorization decision. */
export function roleForPreset(preset: MembershipPreset): "organiser" | "member" {
  return preset === "organiser" ? "organiser" : "member";
}
